import {
  AuditTrailServiceApiAuditTrailServiceCloudEventsAccessSummaryRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupBucketsRequest,
  AuditTrailServiceApiAuditTrailServiceCreateCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceDeleteCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceGetCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupsRequest,
  AuditTrailServiceApiAuditTrailServiceUpdateCloudEventsActivityGroupRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsActivityGraphRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityAccessRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsBucketPathSuggestionsRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourceEventsRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourceHoursRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourceSummaryRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsResourcesRequest,
  AuditTrailServiceApiAuditTrailServiceCloudEventsSummaryRequest,
  AuditTrailServiceApiAuditTrailServiceListCloudEventsResourceIdentitiesRequest,
  AuditTrailServiceApiAuditTrailServiceDownloadCloudEventsActivityAccessRequest,
  V1CloudEventsAccessSummaryResponse,
  V1DownloadCloudEventsActivityAccessResponse,
  V1ListCloudEventsActivityAccessResponse,
  V1CloudEventsActivityGroupBucket,
  V1ListCloudEventsActivityGroupBucketsResponse,
  V1CreateCloudEventsActivityGroupResponse,
  V1GetCloudEventsActivityGroupResponse,
  V1ListCloudEventsActivityGroupsResponse,
  V1CloudEventsActivityGraphResponse,
  V1CloudEventsBucketPathSuggestionsResponse,
  V1CloudEventsResourceEventsResponse,
  V1CloudEventsResourceHoursResponse,
  V1CloudEventsResourceSummaryResponse,
  V1CloudEventsResourcesResponse,
  V1CloudEventsSummaryResponse,
  V1ListCloudEventsResourceIdentitiesResponse,
  V1UpdateCloudEventsActivityGroupResponse,
  V1CloudEventsActivityGroup,
} from '@ariksa/audittrail/api';
import {
  CanAccessResourceSummaryCounts,
  ContextApiGetRiskContextResRequest,
  InsightApiGetCanAccessResourceSummaryCountsRequest,
  LabelResponse,
  ResourceApiGetInventoryResourcesRequest,
  ResourceInfo,
  RiskContextResponse,
  TagsApiGetLabelsRequest,
  TagsApiGetTagsResourceRequest,
  TagsRead,
} from '@ariksa/inventory-core/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { QueryAction, QueryStatus } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';

import { PageInfo } from 'components/index';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { IncidentResponseState } from './types';

export const initialState: IncidentResponseState = {
  activityGroups: PagedQueryState.init([]),
  activityGroup: QueryState.init({} as V1CloudEventsActivityGroup),
  activityGroupBuckets: QueryState.init(
    [] as V1CloudEventsActivityGroupBucket[],
  ),
  activityGroupBucketsByArn: {},
  activityGroupAction: QueryState.init({}),
  accessSummaryCounts: QueryState.init([] as CanAccessResourceSummaryCounts[]),
  activityGraph: QueryState.init({} as V1CloudEventsActivityGraphResponse),
  activityObjectPaths: QueryState.init(
    {} as V1CloudEventsBucketPathSuggestionsResponse,
  ),
  activityBucketContexts: QueryState.init([] as TagsRead[]),
  activityAccesses: PagedQueryState.init([]),
  activityIdentityCount: QueryState.init(0),
  // Holds the latest activity-accesses CSV export request state.
  activityAccessesDownload: QueryState.init(
    {} as V1DownloadCloudEventsActivityAccessResponse,
  ),
  objectPathsByBucket: {},
  activityResources: QueryState.init([] as ResourceInfo[]),
  resourceIdentities: QueryState.init(
    {} as V1ListCloudEventsResourceIdentitiesResponse,
  ),
  labels: QueryState.init([]),
  riskContext: QueryState.init({}),
  resourceRiskContext: QueryState.init({}),
  resources: PagedQueryState.init([]),
  resourceEvents: PagedQueryState.init([], {
    page: {
      info: { page_size: 100, page_number: 1 },
      totalCount: 0,
    },
  }),
  aggregateResourceEvents: QueryState.init([]),
  cloudEventsSummary: QueryState.init({} as V1CloudEventsSummaryResponse),
  resourceSummary: QueryState.init({} as V1CloudEventsResourceSummaryResponse),
  accessSummary: QueryState.init({} as V1CloudEventsAccessSummaryResponse),
};

const incidentResponseSlice = createSlice({
  name: 'incidentResponse',
  initialState,
  reducers: {
    /*get activity groups*/
    getActivityGroups(
      state,
      action: QueryAction<
        V1ListCloudEventsActivityGroupsResponse,
        AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupsRequest
      >,
    ) {
      state.activityGroups = PagedQueryState.next(
        state.activityGroups,
        action,
        {
          mapData: r => r?.results ?? [],
          mapTotalCount: r => r?.total ?? 0,
        },
      );
    },

    /*update activity groups pagination*/
    updateActivityGroupsPagination(state, action: PayloadAction<PageInfo>) {
      state.activityGroups.page.info = action.payload;
    },

    /*get a single activity group*/
    getActivityGroup(
      state,
      action: QueryAction<
        V1GetCloudEventsActivityGroupResponse,
        AuditTrailServiceApiAuditTrailServiceGetCloudEventsActivityGroupRequest
      >,
    ) {
      state.activityGroup = QueryState.next(state.activityGroup, action, {
        mapData: response => response?.result?.group ?? {},
      });
    },

    resetActivityGroup(state) {
      state.activityGroup = QueryState.init({} as V1CloudEventsActivityGroup);
    },

    /*get buckets for an activity group*/
    getActivityGroupBuckets(
      state,
      action: QueryAction<
        V1ListCloudEventsActivityGroupBucketsResponse,
        AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityGroupBucketsRequest
      >,
    ) {
      state.activityGroupBuckets = QueryState.next(
        state.activityGroupBuckets,
        action,
        { mapData: response => response?.results ?? [] },
      );

      state.activityGroupBucketsByArn = {};
      if (action.payload.status !== QueryStatus.fulfilled) return;

      (action.payload.data?.results ?? []).forEach(bucket => {
        if (bucket.bucket_arn) {
          state.activityGroupBucketsByArn[bucket.bucket_arn] = bucket;
        }
      });
    },

    /*create an activity group*/
    createActivityGroup(
      state,
      action: QueryAction<
        V1CreateCloudEventsActivityGroupResponse,
        AuditTrailServiceApiAuditTrailServiceCreateCloudEventsActivityGroupRequest
      >,
    ) {
      state.activityGroupAction = QueryState.next(
        state.activityGroupAction,
        action,
      );
    },

    /*update an activity group*/
    updateActivityGroup(
      state,
      action: QueryAction<
        V1UpdateCloudEventsActivityGroupResponse,
        AuditTrailServiceApiAuditTrailServiceUpdateCloudEventsActivityGroupRequest
      >,
    ) {
      state.activityGroupAction = QueryState.next(
        state.activityGroupAction,
        action,
      );
    },

    /*delete an activity group*/
    deleteActivityGroup(
      state,
      action: QueryAction<
        object,
        AuditTrailServiceApiAuditTrailServiceDeleteCloudEventsActivityGroupRequest
      >,
    ) {
      state.activityGroupAction = QueryState.next(
        state.activityGroupAction,
        action,
      );
    },

    /*get labels*/
    getLabels(
      state,
      action: QueryAction<LabelResponse[], TagsApiGetLabelsRequest>,
    ) {
      state.labels = QueryState.next(state.labels, action);
    },

    /*get access summary*/
    getAccessSummary(
      state,
      action: QueryAction<
        CanAccessResourceSummaryCounts[],
        InsightApiGetCanAccessResourceSummaryCountsRequest
      >,
    ) {
      state.accessSummaryCounts = QueryState.next(
        state.accessSummaryCounts,
        action,
      );
    },

    /*get cloud events activity graph*/
    getActivityGraph(
      state,
      action: QueryAction<
        V1CloudEventsActivityGraphResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsActivityGraphRequest
      >,
    ) {
      state.activityGraph = QueryState.next(state.activityGraph, action);
    },

    /*get object paths for the selected activity resources*/
    getActivityObjectPaths(
      state,
      action: QueryAction<
        V1CloudEventsBucketPathSuggestionsResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsBucketPathSuggestionsRequest
      >,
    ) {
      state.activityObjectPaths = QueryState.next(
        state.activityObjectPaths,
        action,
      );

      if (action.payload.status !== QueryStatus.fulfilled) return;

      (action.payload.data?.results ?? []).forEach(bucket => {
        if (bucket.bucket_name) {
          state.objectPathsByBucket[bucket.bucket_name] = bucket.paths ?? [];
        }
      });
    },

    /*get contexts for the selected activity bucket*/
    getActivityBucketContexts(
      state,
      action: QueryAction<TagsRead[], TagsApiGetTagsResourceRequest>,
    ) {
      state.activityBucketContexts = QueryState.next(
        state.activityBucketContexts,
        action,
      );
    },

    /*get activity graph table data*/
    getActivityAccesses(
      state,
      action: QueryAction<
        V1ListCloudEventsActivityAccessResponse,
        AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityAccessRequest
      >,
    ) {
      state.activityAccesses = PagedQueryState.next(
        state.activityAccesses,
        action,
        {
          mapData: response => response?.results ?? [],
          mapTotalCount: response =>
            parseInt(String(response?.total_count ?? 0)),
        },
      );
    },

    /*get total identities for the activity graph*/
    getActivityIdentityCount(
      state,
      action: QueryAction<
        V1ListCloudEventsActivityAccessResponse,
        AuditTrailServiceApiAuditTrailServiceListCloudEventsActivityAccessRequest
      >,
    ) {
      state.activityIdentityCount = QueryState.next(
        state.activityIdentityCount,
        action,
        {
          mapData: r => parseInt(r?.total_count) ?? 0,
        },
      );
    },

    /*download activity accesses shown in the activity graph as CSV.
      Added to export the graph data through the audittrail download API.*/
    downloadActivityAccesses(
      state,
      action: QueryAction<
        V1DownloadCloudEventsActivityAccessResponse,
        AuditTrailServiceApiAuditTrailServiceDownloadCloudEventsActivityAccessRequest
      >,
    ) {
      state.activityAccessesDownload = QueryState.next(
        state.activityAccessesDownload,
        action,
      );
    },

    /*update activity graph table pagination*/
    updateActivityAccessesPagination(state, action: PayloadAction<PageInfo>) {
      state.activityAccesses.page.info = action.payload;
    },

    /*get resources for the activity drawer filter*/
    getActivityResources(
      state,
      action: QueryAction<
        ResourceInfo[],
        ResourceApiGetInventoryResourcesRequest
      >,
    ) {
      state.activityResources = QueryState.next(
        state.activityResources,
        action,
      );
    },

    /*get identities observed for an activity resource*/
    getResourceIdentities(
      state,
      action: QueryAction<
        V1ListCloudEventsResourceIdentitiesResponse,
        AuditTrailServiceApiAuditTrailServiceListCloudEventsResourceIdentitiesRequest
      >,
    ) {
      state.resourceIdentities = QueryState.next(
        state.resourceIdentities,
        action,
      );
    },

    /*get resources*/
    getCloudEventsResources(
      state,
      action: QueryAction<
        V1CloudEventsResourcesResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsResourcesRequest
      >,
    ) {
      state.resources = PagedQueryState.next(state.resources, action, {
        mapTotalCount: r => parseInt(r?.total || 0),
        mapData: r => r?.results || [],
      });
    },

    /*update resources pagination*/
    updateResourcesPagination(state, action: PayloadAction<PageInfo>) {
      state.resources.page.info = action.payload;
    },

    /*get cloud events summary*/
    getCloudEventsSummary(
      state,
      action: QueryAction<
        V1CloudEventsSummaryResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsSummaryRequest
      >,
    ) {
      state.cloudEventsSummary = QueryState.next(
        state.cloudEventsSummary,
        action,
      );
    },

    /*get cloud events resource summary*/
    getCloudEventsResourceSummary(
      state,
      action: QueryAction<
        V1CloudEventsResourceSummaryResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsResourceSummaryRequest
      >,
    ) {
      state.resourceSummary = QueryState.next(state.resourceSummary, action);
    },

    /*get cloud events access summary*/
    getCloudEventsAccessSummary(
      state,
      action: QueryAction<
        V1CloudEventsAccessSummaryResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsAccessSummaryRequest
      >,
    ) {
      state.accessSummary = QueryState.next(state.accessSummary, action);
    },

    /*get risk context*/
    getRiskContext(
      state,
      action: QueryAction<
        Record<string, RiskContextResponse[]>,
        ContextApiGetRiskContextResRequest
      >,
    ) {
      state.riskContext = QueryState.next(state.riskContext, action);
    },

    /*get resource risk context*/
    getResourceRiskContext(
      state,
      action: QueryAction<
        Record<string, RiskContextResponse[]>,
        ContextApiGetRiskContextResRequest
      >,
    ) {
      state.resourceRiskContext = QueryState.next(
        state.resourceRiskContext,
        action,
      );
    },

    /*get aggregate resource events*/
    getAggregateResourceEvents(
      state,
      action: QueryAction<
        V1CloudEventsResourceHoursResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsResourceHoursRequest
      >,
    ) {
      state.aggregateResourceEvents = QueryState.next(
        state.aggregateResourceEvents,
        action,
        {
          mapData: r => r?.results || [],
        },
      );
    },

    /*get resource events*/
    getResourceEvents(
      state,
      action: QueryAction<
        V1CloudEventsResourceEventsResponse,
        AuditTrailServiceApiAuditTrailServiceCloudEventsResourceEventsRequest
      >,
    ) {
      state.resourceEvents = PagedQueryState.next(
        state.resourceEvents,
        action,
        {
          mapData: r => r?.results || [],
          mapTotalCount: r => r?.total || 0,
        },
      );
    },

    /*update resource events pagination*/
    updateResourceEventsPagination(state, action: PayloadAction<PageInfo>) {
      state.resourceEvents.page.info = action.payload;
    },
  },
});

export const { actions, reducer, name: sliceKey } = incidentResponseSlice;
